import {
  useEffect,
  useCallback,
  useMemo,
  HTMLAttributes,
  SyntheticEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormProvider, Controller } from 'react-hook-form';
import {
  Button,
  Typography,
  Box,
  AutocompleteRenderOptionState,
} from '@mui/material';
import { commonTexts, pdpTexts } from 'i18n';
import PageContainer, {
  PageHeader,
  PageTitle,
  PageTitleWrapper,
} from 'components/PageContainer';
import PageLoader from 'components/PageLoader';
import ReactHookFormField from 'components/ReactHookFormField';
import MultiselectAutocomplete from 'components/MultiselectAutocomplete';
import Select from 'components/Select';
import { Option } from 'store/options';
import usePDPFormValues from 'hooks/pdp/form/usePDPFormValues';
import useDevelopmentNeedsField from 'hooks/pdp/form/useDevelopmentNeedsField';
import useRequiredTrainingsField from 'hooks/pdp/form/useRequiredTrainingsField';
import { useReviewCycleOptions } from 'hooks/reviewCycles';
import useMotivationFactorOptions from 'hooks/pdp/useMotivationFactorOptions';
import { SimpleEmptyState } from '../SimpleEmptyState';
import EnergyLevelField from './EnergyLevelField.component';
import RequiredTrainingForm from './RequiredTrainingForm.component';
import DevelopmentNeedForm from './DevelopmentNeedForm.component';
import MotivationFactorOptionWithInfo from './MotivationFactorOptionWithInfo';
import messages from '../messages';
import {
  FormContainer,
  FormSection,
  ButtonContainer,
  SectionTitle,
  HeaderButton,
  FormFieldsContainer,
  FieldContainer,
  InlineFieldsContainer,
} from './EditPDP.styled';
import { DevelopmentNeedCard } from './DevelopmentNeedForm.styled';

export default function EditPDP() {
  const navigate = useNavigate();
  const { pdpId } = useParams<{ pdpId: string }>();
  const { formatMessage } = useIntl();
  const isEdit = Boolean(pdpId);
  const { form, onSubmitClick, isSubmitting, hydrate, canSubmit } =
    usePDPFormValues({
      pdpId: isEdit && pdpId ? parseInt(pdpId, 10) : undefined,
    });
  const { developmentNeeds, addDevelopmentNeed } =
    useDevelopmentNeedsField(form);
  const { requiredTrainings, addRequiredTraining } =
    useRequiredTrainingsField(form);
  const reviewCycleOptions = useReviewCycleOptions();
  const motivationFactorOptions = useMotivationFactorOptions();

  const reviewCycleSelectOptions = useMemo(
    () =>
      reviewCycleOptions.map((cycle) => ({
        id: cycle.id,
        name: cycle.name,
      })),
    [reviewCycleOptions],
  );

  const motivationFactorOptionsForSelect = useMemo(
    () =>
      motivationFactorOptions.map((option, index) => ({
        id: index,
        name: option.name,
        description: option.description,
      })),
    [motivationFactorOptions],
  );

  const renderMotivationFactorOption = useCallback(
    (
      optionProps: HTMLAttributes<HTMLLIElement>,
      option: Option,
      { selected }: AutocompleteRenderOptionState,
    ) => (
      <MotivationFactorOptionWithInfo
        selected={selected}
        option={option}
        optionProps={optionProps}
      />
    ),
    [],
  );

  const onMotivationFactorsChange = useCallback(
    (e: SyntheticEvent, options: Option[] | null) => {
      if (!options) {
        form.setValue('motivationFactors', []);
        return;
      }
      const selectedIds = options
        .map((option) => {
          const originalOption = motivationFactorOptions[option.id as number];
          return originalOption ? originalOption.name : '';
        })
        .filter(Boolean);
      form.setValue('motivationFactors', selectedIds);
    },
    [form, motivationFactorOptions],
  );

  const removeMotivationFactor = useCallback(
    (itemId: number) => {
      const currentValues = form.getValues('motivationFactors') || [];
      const optionToRemove = motivationFactorOptions[itemId];
      if (optionToRemove) {
        form.setValue(
          'motivationFactors',
          currentValues.filter((id: string) => id !== optionToRemove.name),
        );
      }
    },
    [form, motivationFactorOptions],
  );

  const loadPDP = useCallback(
    async (id: number) => {
      await hydrate(id);
    },
    [hydrate],
  );

  useEffect(() => {
    if (isEdit && pdpId) {
      loadPDP(parseInt(pdpId, 10));
    }
  }, [pdpId, isEdit, loadPDP]);

  const onBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <FormProvider {...form}>
      <PageLoader open={isSubmitting} />
      <PageContainer hasAccess>
        <PageHeader>
          <PageTitleWrapper>
            <PageTitle>
              <FormattedMessage
                {...(isEdit ? pdpTexts.editPDP : pdpTexts.createPDP)}
              />
            </PageTitle>
            <ButtonContainer direction="row">
              <Button
                size="large"
                variant="outlined"
                onClick={onBackClick}
                disabled={isSubmitting}
              >
                <FormattedMessage {...commonTexts.back} />
              </Button>
              <HeaderButton
                size="large"
                variant="contained"
                onClick={onSubmitClick}
                disabled={!canSubmit || isSubmitting}
              >
                <FormattedMessage {...commonTexts.save} />
              </HeaderButton>
            </ButtonContainer>
          </PageTitleWrapper>
        </PageHeader>

        <FormContainer>
          <FormFieldsContainer>
            {/* General Information Section */}
            <FormSection>
              <SectionTitle variant="h5">
                <FormattedMessage {...messages.generalInformation} />
              </SectionTitle>

              <InlineFieldsContainer>
                <ReactHookFormField
                  name="name"
                  type="text"
                  label={<FormattedMessage {...pdpTexts.pdpName} />}
                  placeholder={formatMessage(messages.enterPdpName)}
                  rules={{ required: 'PDP name is required' }}
                  fieldProps={{
                    size: 'medium',
                  }}
                />

                <Controller
                  name="reviewCycleId"
                  control={form.control}
                  rules={{ required: 'Review cycle is required' }}
                  render={({ field, fieldState }) => (
                    <Select
                      {...field}
                      value={field.value ?? null}
                      size="medium"
                      label={<FormattedMessage {...pdpTexts.reviewCycle} />}
                      placeholder={formatMessage(messages.selectReviewCycle)}
                      options={reviewCycleSelectOptions}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      required
                      renderValue={(value) =>
                        reviewCycleSelectOptions.find((opt) => opt.id === value)
                          ?.name || null
                      }
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />
              </InlineFieldsContainer>

              <FieldContainer>
                <ReactHookFormField
                  name="careerAspiration"
                  type="textarea"
                  label={<FormattedMessage {...pdpTexts.careerAspiration} />}
                  placeholder={formatMessage(
                    messages.describeCareerAspirations,
                  )}
                  maxLength={3000}
                  hasLinks
                  fieldProps={{
                    size: 'medium',
                  }}
                />
              </FieldContainer>

              <FieldContainer>
                <Controller
                  name="motivationFactors"
                  control={form.control}
                  render={({ field }) => {
                    const selectedNames = (field.value || []) as string[];
                    const selectedValues =
                      motivationFactorOptionsForSelect.filter((option) => {
                        const originalOption =
                          motivationFactorOptions[option.id as number];
                        return (
                          originalOption &&
                          selectedNames.includes(originalOption.name)
                        );
                      });

                    return (
                      <Box>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ marginBottom: '8px' }}
                        >
                          <FormattedMessage {...pdpTexts.motivationFactors} />
                        </Typography>
                        <MultiselectAutocomplete
                          size="medium"
                          selectedOptions={selectedValues.map(
                            (opt) => opt.id as number,
                          )}
                          options={motivationFactorOptionsForSelect as Option[]}
                          value={selectedValues as Option[]}
                          removeSelection={removeMotivationFactor}
                          onChange={onMotivationFactorsChange}
                          renderOption={renderMotivationFactorOption}
                          showSelectedOptionsNames
                          optionMaxWidth={200}
                          placeholder={formatMessage(
                            messages.selectMotivationFactors,
                          )}
                          disablePortal={false}
                        />
                      </Box>
                    );
                  }}
                />
              </FieldContainer>

              <FieldContainer>
                <EnergyLevelField />
              </FieldContainer>

              <FieldContainer>
                <ReactHookFormField
                  name="currentStrengthAndImprovements"
                  type="textarea"
                  label={
                    <FormattedMessage
                      {...pdpTexts.currentStrengthAndImprovements}
                    />
                  }
                  placeholder={formatMessage(
                    messages.describeStrengthsAndImprovements,
                  )}
                  maxLength={3000}
                  hasLinks
                  fieldProps={{
                    size: 'medium',
                  }}
                />
              </FieldContainer>
            </FormSection>

            {/* Development Needs Section */}
            <FormSection>
              <SectionTitle variant="h5">
                <FormattedMessage {...pdpTexts.developmentNeeds} />
              </SectionTitle>

              {developmentNeeds.length === 0 ? (
                <SimpleEmptyState
                  title={formatMessage(messages.noDevelopmentNeedsTitle)}
                  description={formatMessage(
                    messages.noDevelopmentNeedsDescription,
                  )}
                />
              ) : (
                developmentNeeds.map((field, needIndex) => (
                  <DevelopmentNeedCard key={String((field as any).key)}>
                    <DevelopmentNeedForm needIndex={needIndex} />
                  </DevelopmentNeedCard>
                ))
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={addDevelopmentNeed}
                sx={{ marginTop: 1 }}
              >
                <FormattedMessage {...pdpTexts.addDevelopmentNeed} />
              </Button>
            </FormSection>

            {/* Required Trainings Section */}
            <FormSection>
              <SectionTitle variant="h5">
                <FormattedMessage {...pdpTexts.requiredTrainings} />
              </SectionTitle>

              {requiredTrainings.length === 0 ? (
                <SimpleEmptyState
                  title={formatMessage(messages.noRequiredTrainingsTitle)}
                  description={formatMessage(
                    messages.noRequiredTrainingsDescription,
                  )}
                />
              ) : (
                requiredTrainings.map((field, trainingIndex) => (
                  <RequiredTrainingForm
                    key={String((field as any).key)}
                    trainingIndex={trainingIndex}
                    isEdit={isEdit}
                  />
                ))
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={addRequiredTraining}
                sx={{ marginTop: 1 }}
              >
                <FormattedMessage {...pdpTexts.addTraining} />
              </Button>
            </FormSection>
            <FormSection>
              <SectionTitle variant="h5">
                <FormattedMessage {...pdpTexts.supportRequired} />
              </SectionTitle>
              <FieldContainer>
                <ReactHookFormField
                  name="supportRequired"
                  type="textarea"
                  placeholder={formatMessage(messages.describeSupportRequired)}
                  maxLength={3000}
                  hasLinks
                  fieldProps={{
                    size: 'medium',
                  }}
                />
              </FieldContainer>
            </FormSection>
          </FormFieldsContainer>
        </FormContainer>
      </PageContainer>
    </FormProvider>
  );
}
